import { Link } from "react-router-dom";
import type { DirectoryLocation } from "@/types/directory";
import SectionHeading from "@/components/base/SectionHeading";
import SafeImage from "@/components/base/SafeImage";
import { getLocationFallbackImage } from "@/utils/fallbackImage";

interface DistrictsSectionProps {
  districts: DirectoryLocation[];
  towns: DirectoryLocation[];
}

export default function DistrictsSection({ districts, towns }: DistrictsSectionProps) {
  return (
    <section className="mx-auto w-full max-w-[1400px] px-6 py-16 md:py-20">
      <SectionHeading
        eyebrow="Explore by district"
        title="Find businesses near you on the Canara coast"
        description="Pick a district to explore towns and localities, then drill down to the exact neighbourhood you need."
        action={{ label: "All locations", to: "/locations" }}
      />

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {districts.map((district) => {
          const districtTowns = towns.filter((town) => town.districtSlug === district.slug);
          return (
            <article
              key={district.id}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
            >
              <Link to={`/location/${district.slug}`} className="relative block h-52 w-full overflow-hidden">
                <SafeImage
                  src={district.image}
                  fallback={getLocationFallbackImage(district)}
                  alt={`${district.name} district in coastal Karnataka`}
                  title={`${district.name} - Coastal Bizz Connect`}
                  className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-primary-950/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="font-heading text-xl font-bold text-background-50">{district.name}</h3>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-background-200">
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-store-3-line text-[13px]" />
                    </span>
                    {district.businessCount.toLocaleString("en-IN")} businesses listed
                  </p>
                </div>
              </Link>

              <div className="flex flex-1 flex-col p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-secondary-700">
                  Popular towns
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {districtTowns.slice(0, 5).map((town) => (
                    <Link
                      key={town.id}
                      to={`/location/${town.slug}`}
                      className="whitespace-nowrap rounded-full bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                    >
                      {town.name}
                    </Link>
                  ))}
                </div>
                <Link
                  to={`/location/${district.slug}`}
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
                >
                  Explore {district.name}
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-right-line text-base" />
                  </span>
                </Link>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}